import { useEffect, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";
import { Logo } from "@/components/Logo";
import { NAV_LINKS } from "@/data/content";
import { handleAnchorClick } from "@/lib/scroll";

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const reduce = useReducedMotion();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Scroll-spy: highlight the nav item for the section currently in view.
  const [active, setActive] = useState("");
  useEffect(() => {
    const sections = NAV_LINKS
      .map((l) => document.getElementById(l.href.replace("#", "")))
      .filter(Boolean);
    if (!sections.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible.length) setActive(visible[0].target.id);
      },
      { rootMargin: "-40% 0px -55% 0px", threshold: [0, 0.2, 0.5, 1] }
    );
    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, []);

  const go = (e, href) => {
    handleAnchorClick(e, href);
    setOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? "glass border-b border-white/10" : "bg-transparent"
      }`}
      data-testid="site-header"
    >
      <nav
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-24 flex items-center justify-between"
        aria-label="Primary"
      >
        <a
          href="#top"
          onClick={(e) => go(e, "#top")}
          className="flex items-center gap-3 rounded-xl"
          data-testid="nav-logo-link"
          aria-label="Aiga Enable home"
        >
          <Logo imgClass="h-14 sm:h-20" />
        </a>

        <ul className="hidden lg:flex items-center gap-0.5">
          {NAV_LINKS.map((l) => {
            const isActive = active === l.href.replace("#", "");
            return (
              <li key={l.href}>
                <a
                  href={l.href}
                  onClick={(e) => go(e, l.href)}
                  aria-current={isActive ? "true" : undefined}
                  className={`group flex items-center gap-1.5 px-3 py-2.5 rounded-full text-sm font-medium transition-colors ${
                    isActive ? "text-white bg-white/[0.07]" : "text-slate-200 hover:text-white hover:bg-white/5"
                  }`}
                  data-testid={`nav-link-${l.href.replace("#", "")}`}
                >
                  <span className={`font-mono text-[10px] transition-colors ${isActive ? "text-teal" : "text-teal/70 group-hover:text-teal"}`}>{l.n}</span>
                  {l.label}
                </a>
              </li>
            );
          })}
        </ul>

        <div className="flex items-center gap-2">
          <a
            href="#a-id"
            onClick={(e) => go(e, "#a-id")}
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-teal px-5 py-2.5 text-sm font-semibold text-navy-900 hover:bg-cyan transition-colors shadow-[0_0_24px_-4px_rgba(0,240,255,0.6)]"
            data-testid="nav-explore-aid-btn"
          >
            Explore A-ID <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </a>
          <button
            type="button"
            className="lg:hidden inline-flex items-center justify-center h-11 w-11 rounded-full text-white bg-white/5 hover:bg-white/10 transition-colors"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-controls="mobile-menu"
            aria-label={open ? "Close menu" : "Open menu"}
            data-testid="nav-menu-toggle"
          >
            {open ? <X className="h-6 w-6" aria-hidden="true" /> : <Menu className="h-6 w-6" aria-hidden="true" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            id="mobile-menu"
            className="lg:hidden glass border-t border-white/10 overflow-hidden"
            initial={reduce ? false : { height: 0, opacity: 0 }}
            animate={reduce ? {} : { height: "auto", opacity: 1 }}
            exit={reduce ? {} : { height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          >
            <ul className="px-4 py-4 space-y-1">
              {NAV_LINKS.map((l) => {
                const isActive = active === l.href.replace("#", "");
                return (
                  <li key={l.href}>
                    <a
                      href={l.href}
                      onClick={(e) => go(e, l.href)}
                      aria-current={isActive ? "true" : undefined}
                      className={`flex items-center gap-3 px-4 py-3.5 rounded-xl text-base font-medium transition-colors ${
                        isActive ? "bg-white/[0.07] text-white" : "text-slate-100 hover:bg-white/5"
                      }`}
                      data-testid={`mobile-nav-link-${l.href.replace("#", "")}`}
                    >
                      <span className={`font-mono text-xs ${isActive ? "text-teal" : "text-teal/70"}`}>{l.n}</span>
                      {l.label}
                    </a>
                  </li>
                );
              })}
              <li className="pt-2">
                <a
                  href="#a-id"
                  onClick={(e) => go(e, "#a-id")}
                  className="flex items-center justify-center gap-2 rounded-full bg-teal px-5 py-3.5 text-base font-semibold text-navy-900"
                  data-testid="mobile-explore-aid-btn"
                >
                  Explore A-ID <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </a>
              </li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
